import { ProductImageGenerator } from '@zakeke/zakeke-resizable-canvas';
import _ from 'lodash';
import * as paper from 'paper';
import theme from 'src/shared/theme';
import { Area, AreaDesignerMode, AreaLayer } from './areaDesigner.model';
import { Tool, Unit, createRulerLine, getContrast, getItemChildrens, getItemParents, units, updateRulerLine } from './helpers';
import { DeleteIcon, MoveIcon, ResizeIcon, RotateIcon } from './icons';
import { AreaDesignerAreaSizePlugin } from './plugins/areaDesignerAreaSizePlugin';
import { AreaDesignerGridCheckerPlugin } from './plugins/areaDesignerGridCheckerPlugin';
import { AreaDesignerLayersPlugin } from './plugins/areaDesignerLayersPlugin';
import AreaDesignerPlugin from './plugins/areaDesignerPlugin';
import { AreaDesignerToolToolipPlugin } from './plugins/areaDesignerToolTooltipPlugin';

export interface AreaDesignerEvents {
    onAreaChanged?: (area: Area, newPPCM: number | null, layers: AreaLayer[]) => void;
    onAreaRemoved?: (area: Area) => void;
    onAreaCreated?: (item: paper.Item) => void;
    onAreaSelected?: (area?: Area | null) => void;
    onPPCMChanged?: (ppcm: number) => void;
    onShowRulerInput?: (lineLength: number, x: number, y: number) => void;
    onHideRulerInput?: () => void;
}

export class AreaDesignerManager {
    private HANDLE_COLOR = new paper.Color('#EEE');
    private HANDLE_ICON_COLOR = new paper.Color(theme.app.primary);
    private HANDLE_HOVER_COLOR = new paper.Color(theme.app.primary);

    private AREA_STROKE_COLOR_DARK = new paper.Color('#000');
    private AREA_STROKE_COLOR_LIGHT = new paper.Color('#DDD');
    private AREA_STROKE_WIDTH = 1;
    private AREA_STROKE_WIDTH_SELECTED = 2;

    private layersPlugin!: AreaDesignerLayersPlugin;

    private plugins = [
        new AreaDesignerGridCheckerPlugin(),
        new AreaDesignerAreaSizePlugin(),
        new AreaDesignerToolToolipPlugin(),
        new AreaDesignerLayersPlugin()
    ];

    private canvas!: HTMLCanvasElement;
    private context!: RenderingContext;
    private areasLayer!: paper.Layer;
    private handlesLayer!: paper.Layer;

    private sideImage: paper.Raster | null = null;
    private resizableSideImage: paper.Item | null = null;

    private handles: paper.Group[] = [];
    private handleContainer!: paper.Group;
    private handlesBorderPath!: paper.Path;

    private isRotating = false;
    private isResizing = false;
    private isMoving = false;
    private isMovingView = false;
    private hasViewMoved = false;

    public areas: Area[] = [];
    public ppcm: number = 0;
    public unit: Unit = units[0];

    private originalAreaBounds = new paper.Rectangle(0, 0, 1, 1);
    private originalPPCM = 0;
    private panDeltaFromCenter = new paper.Point(0, 0);

    private events!: AreaDesignerEvents;

    private lastViewCenter: paper.Point | null = null;
    private lastMousePoint: paper.Point | null = null;
    private canvasSize: paper.Size = new paper.Size(1, 1);
    private tool: Tool = 'none';

    private snapXPath: paper.Path | null = null;
    private snapYPath: paper.Path | null = null;
    private currentNewArea: paper.Item | null = null;
    private currentRuleLine: paper.Item[] | null = null;

    private isResizable = false;
    private currentResizableSettings = {
        width: 1,
        height: 1,
        columns: 1,
        rows: 1
    };

    private productImageGenerator = new ProductImageGenerator();

    public selectedItem: paper.Item | null | undefined = null;
    public selectedArea: Area | null | undefined = null;

    public paper = new paper.PaperScope();
    public panTool = new paper.Tool();
    public rectangleTool = new paper.Tool();
    public circleTool = new paper.Tool();
    public polygonTool = new paper.Tool();
    public rulerTool = new paper.Tool();
    public readonly mode: AreaDesignerMode;

    private hitOptions = {
        tolerance: 0,
        segments: false,
        stroke: false,
        fill: true,
        curves: false
    };

    private hitOptionsStroke = {
        tolerance: 5,
        segments: false,
        fill: false,
        stroke: false,
        curves: true
    };

    constructor(mode: AreaDesignerMode) {
        this.mode = mode;
    }

    // Factory method
    public static create(canvas: HTMLCanvasElement, mode: AreaDesignerMode) {
        const manager = new AreaDesignerManager(mode);
        manager.setup(canvas);

        return manager;
    }

    public destroy = () => {
        this.paper.project.remove();
    };

    // Init / setup
    private setup(canvas: HTMLCanvasElement) {
        this.canvas = canvas;
        this.context = canvas.getContext('2d')!;
        this.context.imageSmoothingQuality = 'high';
        this.context.imageSmoothingEnabled = true;

        this.paper.setup(canvas);

        this.areasLayer = new paper.Layer();
        this.handlesLayer = new paper.Layer();

        this.areasLayer.activate();

        this.layersPlugin = this.plugins.find((x) => x instanceof AreaDesignerLayersPlugin)! as AreaDesignerLayersPlugin;

        if (this.mode === 'EDITOR') {
            this.rulerTool.onMouseDown = this.onRulerMouseDown;
            this.rulerTool.onMouseDrag = this.onRulerMouseDrag;
            this.rulerTool.onMouseUp = this.onRulerMouseUp;
            this.rulerTool.onMouseMove = this.onRulerMouseMove;

            this.panTool.onMouseDown = this.onPanMouseDown;
            this.panTool.onMouseMove = this.onPanMouseMove;
            this.panTool.onMouseUp = this.onPanMouseUp;
            this.panTool.onKeyDown = this.onPanKeyDown;

            this.rectangleTool.onMouseDrag = this.onRectangleMouseDrag;
            this.rectangleTool.onMouseUp = this.onAddAreaMouseUp;
            this.rectangleTool.onMouseMove = this.onAddAreaMouseMove;

            this.circleTool.onMouseDrag = this.onCircleMouseDrag;
            this.circleTool.onMouseUp = this.onAddAreaMouseUp;
            this.circleTool.onMouseMove = this.onAddAreaMouseMove;

            this.polygonTool.onMouseDown = this.onPolygonMouseDown;
            this.polygonTool.onMouseUp = this.onPolygonMouseUp;
            this.polygonTool.onMouseMove = this.onPolygonMouseMove;
            this.polygonTool.onKeyDown = this.onPolygonKeyDown;

            this.createHandles();
        }

        this.callPlugin((plugin) => plugin.create(this));

        this.areasLayer.activate(); // Avoid activation of plugins custom layers

        window.addEventListener('resize', this.updateCanvasSize);
        this.updateCanvasSize();
        this.paper.view.center = new paper.Point(0, 0);
    }

    // Set resizable
    public updateResizable = (isResizable: boolean, width: number, height: number, columns: number, rows: number) => {
        this.isResizable = isResizable;

        if (this.sideImage) this.sideImage.visible = !isResizable;

        if (isResizable && this.areas.length > 0) {
            this.setResizableSideImage(width, height, columns, rows);
        } else {
            this.resizableSideImage?.remove();
        }

        this.paper.view.update();
    };

    // Change ppcm
    public setPPCM = (ppcm: number) => {
        if (ppcm != this.ppcm) {
            this.ppcm = ppcm;
        }
    };

    // Keep updated events
    public setEvents = (events: AreaDesignerEvents) => {
        this.events = events;
    };

    public setUnit = (unit: Unit) => {
        if (unit.value != this.unit.value) {
            this.unit = unit;
            this.callPlugin((plugin) => plugin.onUnitChange?.());
        }
    };

    public setTool = (tool: Tool) => {
        if (this.tool != tool) {
            this.cleanToolsElements(this.tool, tool);

            switch (tool) {
                case 'ruler':
                    this.rulerTool.activate();
                    break;
                case 'pan':
                    this.panTool.activate();
                    break;
                case 'rectangle':
                    this.rectangleTool.activate();
                    break;
                case 'circle':
                    this.circleTool.activate();
                    break;
                case 'polygon':
                    this.polygonTool.activate();
                    break;
            }

            this.callPlugin((plugin) => plugin.onToolChanged?.(this.tool, tool));
            this.tool = tool;
        }
    };

    // Keep updated areas
    public setAreas = (areas: Area[]) => {
        this.areas = areas;
        let areasChanged = false;

        // Remove areas that does not exist
        for (const item of this.getAreasItems()) {
            const area = this.getAreaByGuid(item.data.guid);

            if (!area) {
                item.remove();
                this.callPlugin((cb) => cb.onAreaItemRemoved?.(item, item.data.guid));
            }
        }

        // Add new areas
        for (const area of this.areas) {
            let item = this.getItemByGuid(area.guid);
            if (!item) {
                item = this.createItemForArea(area);
                areasChanged = true;

                if (item) {
                    this.callPlugin((cb) => cb.onAreaItemCreated?.(item!, area));
                }
            } else {
                if (this.isAreaChanged(item, area)) {
                    item.remove(); // Remove the old one, types may be different
                    item = this.areasLayer.importJSON(area.path);
                    item.data.guid = area.guid;
                    item.data.isArea = true;

                    areasChanged = true;

                    if (item) this.callPlugin((cb) => cb.onAreaItemUpdated?.(item!, area));
                }
            }
        }

        if (areasChanged) {
            this.updateAreasColors();
        }
    };

    private isAreaChanged = (item: paper.Item, area: Area) => {
        const sizeChanged =
            Math.abs(item.bounds.x - area.boundsX) > 0.01 ||
            Math.abs(item.bounds.y - area.boundsY) > 0.01 ||
            Math.abs(item.bounds.width - area.boundsWidth) > 0.01 ||
            Math.abs(item.bounds.height - area.boundsHeight) > 0.01;

        return sizeChanged;
    };

    // Keep selected area updated
    public setSelectedArea = (guid?: string | null) => {
        const previousSelectedArea = this.selectedArea;
        const previousSelectedItem = this.selectedItem;

        this.selectedArea = guid ? this.areas.find((area) => area.guid == guid) : null;
        this.selectedItem = guid ? this.getItems().find((item) => item.data.guid == guid) : null;

        this.updateAreasColors();

        this.callPlugin((plugin) => {
            if (this.selectedArea && this.selectedItem) plugin.onAreaSelectionChanged?.(this.selectedItem, this.selectedArea, true);

            if (previousSelectedArea && previousSelectedItem) plugin.onAreaSelectionChanged?.(previousSelectedItem, previousSelectedArea, false);
        });
    };

    public setSideImage = async (url: string) => {
        return new Promise<void>((resolve) => {
            if (this.sideImage && this.sideImage.source === url) {
                resolve();
                return;
            }

            if (!this.sideImage) {
                this.sideImage = new paper.Raster({ crossOrigin: 'anonymous' });
                this.sideImage.onLoad = () => {
                    this.sideImage!.position = new paper.Point(0, 0);
                    this.areasLayer.addChild(this.sideImage!);
                    this.sideImage!.sendToBack();
                    this.fitToBounds();
                    this.updateAreasColors();
                    this.update();
                    this.callPlugin((plugin) => plugin.onSideImageChanged?.(this.sideImage));
                    this.paper.view.update();

                    resolve();
                };
            }

            this.sideImage.source = url;
        });
    };

    public setResizableSideImage = async (width: number, height: number, columns: number, rows: number) => {
        this.currentResizableSettings = { width, height, columns, rows };

        if (this.resizableSideImage) {
            this.resizableSideImage.remove();
            this.resizableSideImage = null;
        }

        const { shape, splitColumns, measures } = await this.productImageGenerator.generateImage({
            width,
            height,
            scale: 1 / this.paper.view.zoom,
            shape: {
                type: 'rectangle',
                borderRadius: 10,
                borderColor: '#000000',
                borderWidth: 1,
                fillColor: '#FFFFFF',
                shadowColor: '#00000055',
                shadowBlur: 5,
                shadowOffset: {
                    x: 0,
                    y: 10
                }
            },
            splitColumns: {
                rows,
                columns,
                lineColor: '#CCC',
                lineWidth: 1,
                lineDashArray: [10, 10],
                marginHorizontal: 10,
                marginVertical: 10
            },
            measures: {
                lineColor: '#888',
                lineWidth: 1,
                lineDashArray: [10, 10],
                widthDistance: 10,
                heightDistance: 10,
                widthLabel: Math.round((width / this.ppcm) * 100) / 100 + ' cm',
                heightLabel: Math.round((height / this.ppcm) * 100) / 100 + ' cm',
                labelFontSize: 11,
                labelColor: '#455a64'
            }
        });

        const shapeItem = this.areasLayer.importJSON(shape.json);
        const splitColumnsItem = this.areasLayer.importJSON(splitColumns.json);
        const measuresItem = this.areasLayer.importJSON(measures.json);

        const sideImage = new this.paper.Group();
        sideImage.addChild(shapeItem);
        sideImage.addChild(splitColumnsItem);
        sideImage.addChild(measuresItem);

        this.areasLayer.addChild(sideImage);

        this.resizableSideImage = sideImage;
        this.resizableSideImage.sendToBack();
    };

    public zoomIn = () => {
        this.paper.view.zoom *= 1.1;
        this.callPlugin((plugin) => plugin.onViewZoomChanged?.());
        this.update();
    };

    public zoomOut = () => {
        this.paper.view.zoom /= 1.1;
        this.callPlugin((plugin) => plugin.onViewZoomChanged?.());
        this.update();
    };

    // The method first fit the zoom on the area + side image
    // then renders everything so that the size labels are updated base on zoom
    // then recalculates the bounds of all items and finally zooms out a bit
    public fitToBounds = () => {
        let bounds: paper.Rectangle = new paper.Rectangle({ x: 0, y: 0, width: 1, height: 1 });

        for (const item of this.getItems()) {
            bounds = bounds.unite(item.bounds);
        }

        let zoom = Math.min(this.paper.view.bounds.width / bounds.width, this.paper.view.bounds.height / bounds.height);

        this.paper.view.zoom = zoom;
        this.paper.view.center = bounds.center;

        this.callPlugin((plugin) => plugin.onViewZoomChanged?.());
        this.paper.view.update();
        bounds = new paper.Rectangle({ x: 0, y: 0, width: 1, height: 1 });

        // Recalculate bounds after zoom
        for (const item of this.getItems()) {
            bounds = bounds.unite(item.bounds);
        }

        // Add all items from layer 'area-size-labels'
        for (const item of this.paper.project.layers.find((layer) => layer.name === 'area-size-labels')?.children || []) {
            console.log(item);
            bounds = bounds.unite(item.bounds);
        }

        zoom = Math.min(
            this.paper.view.bounds.width / (bounds.width + bounds.width * 0.1),
            this.paper.view.bounds.height / (bounds.height + bounds.height * 0.1)
        );

        this.paper.view.zoom *= zoom;
        this.paper.view.center = bounds.center;

        this.callPlugin((plugin) => plugin.onViewZoomChanged?.());

        this.update();
    };

    public update = () => {
        this.hitOptionsStroke.tolerance = Math.max(10, Math.round(10 / this.paper.view.zoom));
        this.resetHandles();
        this.updateResizable(
            this.isResizable,
            this.currentResizableSettings.width,
            this.currentResizableSettings.height,
            this.currentResizableSettings.columns,
            this.currentResizableSettings.rows
        );
    };

    // Internal methods

    private cleanToolsElements = (prevTool: Tool, newTool: Tool) => {
        // Clear the rule arrow
        if (this.currentRuleLine !== null) {
            this.currentRuleLine.forEach((x) => x.remove());
            this.currentRuleLine = null;

            this.events.onHideRulerInput?.();
        }

        if (prevTool === 'polygon') {
            this.currentNewArea?.remove();
            this.currentNewArea = null;
        }
    };

    private callPlugin = (cb: (plugin: AreaDesignerPlugin) => void) => {
        this.plugins.forEach(cb);
    };

    private createHandles = () => {
        const rect = new paper.Rectangle({ x: 0, y: 0, width: 30, height: 30 });
        const radius = new paper.Size(30, 30);

        const moveHandle = new paper.Group();
        const deleteHandle = new paper.Group();
        const resizeHandle = new paper.Group();
        const rotateHandle = new paper.Group();

        const moveRectangle = new paper.Path.Rectangle(rect, radius);
        const deleteRectangle = new paper.Path.Rectangle(rect, radius);
        const resizeRectangle = new paper.Path.Rectangle(rect, radius);
        const rotateRectangle = new paper.Path.Rectangle(rect, radius);

        const moveIcon = this.paper.project.importSVG(MoveIcon);
        const deleteIcon = this.paper.project.importSVG(DeleteIcon);
        const rotateIcon = this.paper.project.importSVG(RotateIcon);
        const resizeIcon = this.paper.project.importSVG(ResizeIcon);
        resizeIcon.rotate(45);

        moveHandle.addChildren([moveRectangle, moveIcon]);
        deleteHandle.addChildren([deleteRectangle, deleteIcon]);
        resizeHandle.addChildren([resizeRectangle, resizeIcon]);
        rotateHandle.addChildren([rotateRectangle, rotateIcon]);

        const handles = [moveHandle, resizeHandle, deleteHandle, rotateHandle];

        [moveRectangle, deleteRectangle, resizeRectangle, rotateRectangle].forEach((rectangle) => {
            rectangle.fillColor = this.HANDLE_COLOR;
            rectangle.strokeWidth = 1;
            rectangle.strokeScaling = false;
            rectangle.strokeColor = new paper.Color('#CCC');
            rectangle.data.isHandleRectangle = true;
        });

        [moveIcon, deleteIcon, resizeIcon, rotateIcon].forEach((icon) => {
            icon.fillColor = this.HANDLE_ICON_COLOR;
            icon.position = new paper.Point(15, 15);
            icon.scale(0.6);

            getItemChildrens(icon).forEach((child) => {
                child.data.isHandleIcon = true;
            });
        });

        handles.forEach((handle) => {
            handle.applyMatrix = false;
            handle.data.isHandle = true;
        });

        this.handleContainer = new this.paper.Group();
        this.handleContainer.addChildren(handles);

        this.handlesLayer.addChild(this.handleContainer);

        this.handles = handles;
    };

    private applyAreaStyle = (item: paper.Item) => {
        if (this.sideImage) {
            item.strokeColor = getContrast(item.bounds, this.sideImage) ? this.AREA_STROKE_COLOR_DARK : this.AREA_STROKE_COLOR_LIGHT;
            item.dashArray = [5, 5];
            item.strokeWidth = this.AREA_STROKE_WIDTH;
            item.strokeScaling = false;
            item.applyMatrix = true;
        }
    };

    private updateAreasColors = () => {
        // Update area stroke width and color
        for (const item of this.getAreasItems()) {
            this.applyAreaStyle(item);
        }

        if (this.selectedItem) {
            this.selectedItem.strokeWidth = this.AREA_STROKE_WIDTH_SELECTED;
        }
    };

    private resetHandles = () => {
        if (this.selectedItem && this.selectedArea) {
            this.handleContainer.rotation = 0;
            this.handleContainer.position = this.selectedItem.position;

            this.handles[0].position = this.selectedItem.bounds.topLeft;
            this.handles[1].position = this.selectedItem.bounds.bottomRight;
            this.handles[2].position = this.selectedItem.bounds.bottomLeft;
            this.handles[3].position = this.selectedItem.bounds.topRight;

            this.handles.forEach((handle) => {
                handle.scaling = new this.paper.Point(1 / this.paper.view.zoom, 1 / this.paper.view.zoom);
                handle.visible = true;
                handle.bringToFront();
            });

            // Hide rotation for areas with layers
            this.handles[3].visible = this.selectedArea!.layers.length === 0;

            if (this.handlesBorderPath) {
                this.handlesBorderPath.remove();
            }

            this.handlesBorderPath = new paper.Path.Rectangle(this.selectedItem.bounds);
            this.handleContainer.addChild(this.handlesBorderPath);
            this.handlesBorderPath.strokeWidth = 1;
            this.handlesBorderPath.strokeScaling = false;
            this.handlesBorderPath.strokeColor = new paper.Color(0.5, 0.5, 0.5);
            this.handlesBorderPath.dashArray = [10, 10];
            this.handlesBorderPath.applyMatrix = false;
            this.handlesBorderPath.sendToBack();
            this.handlesLayer.bringToFront();
        } else {
            this.handlesBorderPath?.remove();
            this.handles.forEach((handle) => (handle.visible = false));
        }
    };

    // Better to use the .rotate method because we can't get the current area rotation
    // because we are using applyMatrix = true so the rotation is stored in the path and
    // cannot be retrevied
    private updateHandles = (deltaAngle: number, position: paper.Point, moveHandlesToBounds: boolean) => {
        if (this.selectedItem && this.selectedArea) {
            this.handleContainer.rotate(deltaAngle);
            this.handleContainer.position = position;

            for (const handle of this.handles) handle.rotation = 0;

            if (moveHandlesToBounds) {
                const scaleX = this.selectedItem.bounds.width / this.handlesBorderPath.bounds.width;
                const scaleY = this.selectedItem.bounds.height / this.handlesBorderPath.bounds.height;

                this.handlesBorderPath.scale(scaleX, scaleY);
                this.handlesBorderPath.position = position;

                this.handles[0].position = this.selectedItem.bounds.topLeft;
                this.handles[1].position = this.selectedItem.bounds.bottomRight;
                this.handles[2].position = this.selectedItem.bounds.bottomLeft;
                this.handles[3].position = this.selectedItem.bounds.topRight;
            }
        }
    };

    private isHandle = (item: paper.Item) => {
        return item.data.isHandle || item.data.isHandleIcon || item.data.isHandleRectangle;
    };

    private getHandle = (item: paper.Item) => {
        if (this.isHandle(item)) {
            return item.data.isHandleIcon || item.data.isHandleRectangle ? getItemParents(item).find((x) => x.data.isHandle) : item;
        } else {
            return null;
        }
    };

    public updateCanvasSize = () => {
        if (this.canvas && this.paper) {
            this.canvasSize = new paper.Size(
                this.canvas.parentNode ? (this.canvas.parentNode as HTMLElement).offsetWidth : this.canvas.width,
                this.canvas.parentNode ? (this.canvas.parentNode as HTMLElement).offsetHeight : this.canvas.height
            );
            this.paper.view.viewSize = this.canvasSize;

            this.callPlugin((plugin) => {
                plugin.onViewMoved?.();
                plugin.onViewZoomChanged?.();
            });
        }
    };

    public hideRuler = () => {
        this.cleanToolsElements(this.tool, this.tool);
    };

    public getItems = () => {
        return this.areasLayer.children as paper.Item[];
    };

    public getAreasItems = () => {
        return this.getItems().filter((item) => item.data && item.data.isArea);
    };

    public updateArea = (item: paper.Item, newPPCM: number | null) => {
        const area = this.areas.find((area) => area.guid == item.data.guid);

        if (!area) throw new Error(`Can't update area ${item.data.guid}: area not found.`);

        this.callPlugin((cb) => cb.onAreaItemUpdated?.(item!, area));

        // First we clone the area item so that we can get a clean json that is not depend of the currenct graphics
        // E.g without stroke info, ecc

        const clonedItem = item.clone();
        clonedItem.strokeWidth = 1;
        clonedItem.strokeColor = this.AREA_STROKE_COLOR_LIGHT;
        clonedItem.strokeScaling = true;

        const clonedArea = _.clone(area);
        clonedArea.path = clonedItem.exportJSON();
        clonedArea.pathSVG = clonedItem.exportSVG({ asString: true }) as string;
        clonedArea.boundsX = item.bounds.x;
        clonedArea.boundsY = item.bounds.y;
        clonedArea.boundsWidth = item.bounds.width;
        clonedArea.boundsHeight = item.bounds.height;

        clonedItem.remove();
        this.events.onAreaChanged?.(clonedArea, newPPCM, this.layersPlugin.getUpdatedLayers(area));
    };

    public getItemByGuid = (guid: string) => {
        return this.getItems().find((item) => item.data.guid == guid);
    };

    public getAreaByGuid = (guid: string) => {
        return this.areas.find((area) => area.guid == guid);
    };

    private createItemForArea = (area: Area) => {
        const item = this.areasLayer.importJSON(area.path);
        item.data.guid = area.guid; // Force guid update
        item.data.isArea = true;

        this.applyAreaStyle(item);

        return item;
    };

    private getRootItem = (item: paper.Item) => {
        let parent = item;

        while (!parent.data || !parent.data.guid) {
            if (parent.parent) parent = parent.parent;
            else break;
        }

        return parent;
    };

    private hitTest = (point: paper.Point) => {
        const hitPoint = this.handlesLayer.hitTest(point, this.hitOptions);
        const handleItem = hitPoint && hitPoint.item && this.isHandle(hitPoint.item) ? this.getHandle(hitPoint.item) : null;

        const hitPointStroke = this.areasLayer.hitTest(point, this.hitOptionsStroke);
        const areaRootItem = hitPointStroke?.item ? this.getRootItem(hitPointStroke.item) : null;
        const areaItem = areaRootItem?.data?.isArea ? areaRootItem : null;

        return [handleItem, areaItem];
    };

    // Snap
    private getSnapPosition = (point: paper.Point) => {
        const snapAreasItems = this.getAreasItems().filter((x) => x.data.guid !== this.selectedArea?.guid);
        const newPoint = new paper.Point(point);
        let snappedX = false;
        let snappedY = false;

        if (this.selectedItem) {
            const snapPointsX = [0, ...snapAreasItems.map((x) => x.bounds.center.x)];

            const snapPointsY = [0, ...snapAreasItems.map((x) => x.bounds.center.y)];

            for (const snapPointX of snapPointsX) {
                if (Math.abs(snapPointX - point.x) < 5 / this.paper.view.zoom) {
                    newPoint.x = snapPointX;
                    snappedX = true;
                    break;
                }
            }

            for (const snapPointY of snapPointsY) {
                if (Math.abs(snapPointY - point.y) < 5 / this.paper.view.zoom) {
                    newPoint.y = snapPointY;
                    snappedY = true;
                    break;
                }
            }
        }

        return { newPoint, snappedX, snappedY };
    };

    private createSnapGuide = (x1: number, y1: number, x2: number, y2: number) => {
        const path = new this.paper.Path.Line(new this.paper.Point(x1, y1), new this.paper.Point(x2, y2));

        path.strokeWidth = 1;
        path.strokeColor = new this.paper.Color('blue');
        path.strokeWidth = 1.0;
        path.dashOffset = 5;
        path.dashArray = [5, 5];
        path.strokeScaling = false;
        path.removeOn({
            drag: true,
            up: true
        });

        return path;
    };

    // #region Pan tool

    private onPanMouseDown = (e: paper.ToolEvent) => {
        this.lastMousePoint = e.point;

        const [handle, areaItem] = this.hitTest(e.point);

        if (this.selectedItem && handle) {
            this.originalAreaBounds = this.selectedItem.bounds;
            this.originalPPCM = this.ppcm;

            // First check for handles
            if (handle == this.handles[0]) {
                // move
                this.isMoving = true;
                this.panDeltaFromCenter = this.selectedItem.bounds.center.subtract(e.point);
                this.callPlugin((plugin) => plugin.onAreaMoveStart?.(this.selectedItem!, this.selectedArea!, e));
            } else if (handle == this.handles[1]) {
                // resize
                this.isResizing = true;
                this.callPlugin((plugin) => plugin.onAreaResizeStart?.(this.selectedItem!, this.selectedArea!, e));
            } else if (handle == this.handles[2]) {
                // remove
                this.events.onAreaRemoved?.(this.selectedArea!);
                this.callPlugin((plugin) => plugin.onAreaRemoved?.(this.selectedItem!, this.selectedArea!, e));
            } else if (handle == this.handles[3]) {
                // rotate
                this.isRotating = true;
                this.callPlugin((plugin) => plugin.onAreaRotateStart?.(this.selectedItem!, this.selectedArea!, e));
            }

            this.resetHandles();
        } else if (areaItem) {
            // Then for area
            this.panDeltaFromCenter = areaItem.bounds.center.subtract(e.point);
            this.isMoving = true;
            this.originalAreaBounds = areaItem.bounds;
            this.originalPPCM = this.ppcm;
            this.events.onAreaSelected?.(this.areas.find((area) => area.guid == areaItem.data.guid));
            this.callPlugin((plugin) => plugin.onAreaMoveStart?.(this.selectedItem!, this.selectedArea!, e));

            this.resetHandles();
        } else {
            // Then move view
            this.isMovingView = true;
        }

        this.callPlugin((plugin) => plugin.onMouseDown?.(e));
    };

    private onPanMouseUp = (e: paper.ToolEvent) => {
        const [handle, areaItem] = this.hitTest(e.point);

        if (this.selectedArea && this.selectedItem && (this.isResizing || this.isMoving || this.isRotating)) {
            // If bleed lines, change also the PPCM, you can't change their physical size.
            let newPPCM: number | null = null;

            if (this.selectedArea.layers.length > 0) {
                // Get the old physical size, and keep it.
                const scale = this.originalAreaBounds.width / this.selectedItem.bounds.width;
                newPPCM = this.originalPPCM / scale;
            }

            this.updateAreasColors();
            this.updateArea(this.selectedItem, newPPCM);
        } else if (!handle && !areaItem && !this.hasViewMoved) {
            this.events.onAreaSelected?.(null);
        }

        this.callPlugin((plugin) => {
            if (this.isMoving) plugin.onAreaMoveEnd?.(this.selectedItem!, this.selectedArea!, e);

            if (this.isResizing) plugin.onAreaResizeEnd?.(this.selectedItem!, this.selectedArea!, e);

            if (this.isRotating) plugin.onAreaRotateEnd?.(this.selectedItem!, this.selectedArea!, e);

            plugin.onMouseUp?.(e);
        });

        this.isRotating = false;
        this.isResizing = false;
        this.isMoving = false;
        this.isMovingView = false;
        this.hasViewMoved = false;
    };

    private onPanMouseMove = (e: paper.ToolEvent) => {
        for (const handle of this.handles) {
            handle.fillColor = this.HANDLE_COLOR;
            handle.children[1].fillColor = this.HANDLE_ICON_COLOR;
        }

        const [handle, areaItem] = this.hitTest(e.point);

        if (this.canvas) {
            if (handle) {
                handle!.children[0].fillColor = this.HANDLE_HOVER_COLOR;
                handle!.children[1].fillColor = this.HANDLE_COLOR;

                let cursor = 'pointer';

                if (handle == this.handles[0]) cursor = 'move';

                if (handle == this.handles[1]) cursor = 'nw-resize';

                this.canvas.style.cursor = cursor;
            } else if (areaItem && !this.isResizable) {
                this.canvas.style.cursor = 'move';
            } else {
                this.canvas.style.cursor = 'default';
            }
        }

        if (this.selectedItem && this.selectedArea) {
            if (this.isRotating) {
                const dir = e.point.subtract(this.selectedItem.position);
                const prevDir = e.lastPoint.subtract(this.selectedItem.position);
                const angle = prevDir.getDirectedAngle(dir);

                this.selectedItem.rotate(angle);

                this.callPlugin((plugin) => plugin.onAreaRotating?.(this.selectedItem!, this.selectedArea!, e));
                this.updateHandles(angle, this.selectedItem.position, false);
            } else if (this.isResizing) {
                const oldDiagonal = this.selectedItem.bounds.bottomRight.subtract(this.selectedItem.bounds.topLeft);
                const currentDistancePoint = e.point.subtract(this.selectedItem.bounds.topLeft);

                if (currentDistancePoint.x >= 0 && currentDistancePoint.y >= 0) {
                    // Bleed lines must have proportional scaling
                    const isBleedMode = this.selectedArea.layers.length > 0;

                    const scaleX = Math.max(0.1, currentDistancePoint.x / oldDiagonal.x);
                    const scaleY = isBleedMode ? scaleX : Math.max(0.1, currentDistancePoint.y / oldDiagonal.y);

                    this.selectedItem.scale(scaleX, scaleY, this.selectedItem.bounds.topLeft);

                    // Update PPCM in real time when resizing to keep the layers resize accordly
                    if (isBleedMode) {
                        // Get the old physical size, and keep it.
                        const scale = this.originalAreaBounds.width / this.selectedItem.bounds.width;
                        this.ppcm = this.originalPPCM / scale;
                    }

                    this.callPlugin((plugin) => plugin.onAreaResizing?.(this.selectedItem!, this.selectedArea!, e));

                    this.updateHandles(0, this.selectedItem.bounds.center, true);
                }
            } else if (this.isMoving) {
                const targetPosition = e.point.add(this.panDeltaFromCenter);
                const { newPoint, snappedX, snappedY } = this.getSnapPosition(targetPosition);
                this.selectedItem.position = newPoint;

                // Snap lines
                if (this.snapXPath) this.snapXPath.remove();

                if (this.snapYPath) this.snapYPath.remove();

                if (snappedX) {
                    this.snapXPath = this.createSnapGuide(newPoint.x, this.paper.view.bounds.top, newPoint.x, this.paper.view.bounds.bottom);
                }

                if (snappedY) {
                    this.snapYPath = this.createSnapGuide(this.paper.view.bounds.left, newPoint.y, this.paper.view.bounds.right, newPoint.y);
                }

                // Update plugins
                this.callPlugin((plugin) => plugin.onAreaMoving?.(this.selectedItem!, this.selectedArea!, e));
                this.updateHandles(0, this.selectedItem.position, false);
            }
        }

        if (this.isMovingView) {
            this.hasViewMoved = true;
            this.lastViewCenter = this.paper.view.center;

            if (this.lastMousePoint) {
                this.paper.view.center = this.paper.view.center.add(this.lastMousePoint.subtract(e.point));
            }

            this.lastMousePoint = e.point.add(this.paper.view.center.subtract(this.lastViewCenter));
            this.callPlugin((plugin) => plugin.onViewMoved?.());
        }

        this.callPlugin((plugin) => plugin.onMouseMove?.(e));
    };

    private onPanKeyDown = (e: paper.KeyEvent) => {
        if (this.selectedItem && this.selectedArea) {
            switch (e.key) {
                case 'left':
                    this.selectedItem.position.x -= 1 / this.paper.view.zoom;
                    this.callPlugin((plugin) => plugin.onAreaMoving?.(this.selectedItem!, this.selectedArea!, e));
                    break;
                case 'right':
                    this.selectedItem.position.x += 1 / this.paper.view.zoom;
                    this.callPlugin((plugin) => plugin.onAreaMoving?.(this.selectedItem!, this.selectedArea!, e));
                    break;
                case 'up':
                    this.selectedItem.position.y -= 1 / this.paper.view.zoom;
                    this.callPlugin((plugin) => plugin.onAreaMoving?.(this.selectedItem!, this.selectedArea!, e));
                    break;
                case 'down':
                    this.selectedItem.position.y += 1 / this.paper.view.zoom;
                    this.callPlugin((plugin) => plugin.onAreaMoving?.(this.selectedItem!, this.selectedArea!, e));
                    break;
                case 'delete':
                    this.events.onAreaRemoved?.(this.selectedArea);
                    break;
            }

            this.updateArea(this.selectedItem, null);
        }

        this.callPlugin((plugin) => plugin.onKeyDown?.(e));
    };

    // #endregion

    // #region Rectangle tool

    private onRectangleMouseDrag = (e: paper.ToolEvent) => {
        const startPoint = e.downPoint;
        const currentPoint = e.point;

        if (this.currentNewArea === null) {
            this.currentNewArea = new this.paper.Path.Rectangle(startPoint, new this.paper.Size(100, 100));
            this.currentNewArea.data = {};
            this.currentNewArea.data.isArea = true;
            this.applyAreaStyle(this.currentNewArea);
        }

        const oldDiagonal = this.currentNewArea.bounds.bottomRight.subtract(this.currentNewArea.bounds.topLeft);
        const currentDistancePoint = currentPoint.subtract(this.currentNewArea.bounds.topLeft);

        if (currentDistancePoint.x >= 0 && currentDistancePoint.y >= 0) {
            const scaleX = Math.max(0.1, currentDistancePoint.x / oldDiagonal.x);
            const scaleY = Math.max(0.1, currentDistancePoint.y / oldDiagonal.y);

            this.currentNewArea.scale(scaleX, scaleY, this.currentNewArea.bounds.topLeft);

            this.callPlugin((plugin) => plugin.onAreaCreating?.(e));
        }

        this.callPlugin((plugin) => plugin.onMouseMove?.(e));
    };

    private onAddAreaMouseUp = (e: paper.ToolEvent) => {
        if (this.currentNewArea !== null) {
            this.currentNewArea.strokeWidth = 1;
            this.currentNewArea.strokeColor = this.AREA_STROKE_COLOR_LIGHT;
            this.currentNewArea.strokeScaling = true;

            this.events.onAreaCreated?.(this.currentNewArea);
            this.currentNewArea.remove();
            this.currentNewArea = null;
        }

        this.callPlugin((plugin) => plugin.onMouseUp?.(e));
    };

    private onAddAreaMouseMove = (e: paper.ToolEvent) => {
        this.canvas.style.cursor = 'crosshair';
        this.callPlugin((plugin) => plugin.onMouseMove?.(e));
    };

    // #endregion

    // #region Circle tool

    private onCircleMouseDrag = (e: paper.ToolEvent) => {
        const startPoint = e.downPoint;
        const currentPoint = e.point;

        if (this.currentNewArea === null) {
            this.currentNewArea = new this.paper.Path.Circle(startPoint.add(100), 100);
            this.currentNewArea.data = {};
            this.currentNewArea.data.isArea = true;
            this.applyAreaStyle(this.currentNewArea);
        }

        const oldDiagonal = this.currentNewArea.bounds.bottomRight.subtract(this.currentNewArea.bounds.topLeft);
        const currentDistancePoint = currentPoint.subtract(this.currentNewArea.bounds.topLeft);

        if (currentDistancePoint.x >= 0 && currentDistancePoint.y >= 0) {
            const scaleX = Math.max(0.1, currentDistancePoint.x / oldDiagonal.x);
            const scaleY = Math.max(0.1, currentDistancePoint.y / oldDiagonal.y);

            this.currentNewArea.scale(scaleX, scaleY, this.currentNewArea.bounds.topLeft);

            this.callPlugin((plugin) => plugin.onAreaCreating?.(e));
        }

        this.callPlugin((plugin) => plugin.onMouseMove?.(e));
    };

    // #endregion

    // #region Polygon tool

    private onPolygonMouseDown = (e: paper.ToolEvent) => {
        const startPoint = e.downPoint;

        if (this.currentNewArea === null) {
            this.currentNewArea = new this.paper.Path();
            this.currentNewArea.data = {};
            this.currentNewArea.data.isArea = true;
            this.applyAreaStyle(this.currentNewArea);

            (this.currentNewArea as paper.Path).add(startPoint); // Origin
            (this.currentNewArea as paper.Path).add(startPoint); // Last segment we will move
        }
    };

    private onPolygonMouseMove = (e: paper.ToolEvent) => {
        this.onAddAreaMouseMove(e);

        if (this.currentNewArea) {
            (this.currentNewArea as paper.Path).lastSegment.point = e.point;

            this.callPlugin((plugin) => plugin.onAreaCreating?.(e));
        }
    };

    private onPolygonMouseUp = (e: paper.ToolEvent) => {
        if (this.currentNewArea) {
            const currentPath = this.currentNewArea as paper.Path;

            // If we are near the first segment, close the path and add it
            if (currentPath.segments.length > 2 && e.point.isClose(currentPath.firstSegment.point, 10 / this.paper.view.zoom)) {
                currentPath.closed = true;

                var bounds = currentPath.bounds;
                if (bounds.width < 15 || bounds.height < 15) {
                    this.currentNewArea.remove();
                    this.currentNewArea = null;
                } else {
                    // Create the area
                    this.onAddAreaMouseUp(e);
                }
            } else {
                // Just add a new segment
                currentPath.add(e.point);
            }
        }

        this.callPlugin((plugin) => plugin.onMouseUp?.(e));
    };

    private onPolygonKeyDown = (e: paper.KeyEvent) => {
        switch (e.key) {
            case 'escape':
                if (this.currentNewArea) {
                    this.currentNewArea.remove();
                    this.currentNewArea = null;
                }
                break;
        }

        this.callPlugin((plugin) => plugin.onKeyDown?.(e));
    };

    // //#endregion

    // #region Ruler tool

    private onRulerMouseDown = (e: paper.ToolEvent) => {
        // Clear the rule arrow when dragging not on mouse up
        if (this.currentRuleLine !== null) {
            this.currentRuleLine.forEach((x) => x.remove());
            this.currentRuleLine = null;
        }

        this.events.onHideRulerInput?.();
    };

    private onRulerMouseDrag = (e: paper.ToolEvent) => {
        if (this.sideImage) {
            const startPoint = e.downPoint;
            const currentPoint = e.point;

            if (this.currentRuleLine === null) {
                this.currentRuleLine = createRulerLine(this.paper, this.sideImage, startPoint, currentPoint);
            }

            if (this.currentRuleLine) {
                updateRulerLine(this.paper, this.currentRuleLine, this.sideImage, this.ppcm, this.unit, startPoint, currentPoint);
            }

            this.callPlugin((plugin) => plugin.onRulerDragging?.(e));
        }

        this.callPlugin((plugin) => plugin.onMouseMove?.(e));
    };

    private onRulerMouseUp = (e: paper.ToolEvent) => {
        if (this.currentRuleLine) {
            const point = this.paper.view.projectToView((this.currentRuleLine[0] as paper.Path).lastSegment.point);
            this.events.onShowRulerInput?.((this.currentRuleLine[0] as paper.Path).length, point.x, point.y);
        } else {
            this.events.onShowRulerInput?.(0, 0, 0); // This will trigger a warning about the user to click and drag
        }
    };

    private onRulerMouseMove = (e: paper.ToolEvent) => {
        this.canvas.style.cursor = 'crosshair';

        this.callPlugin((plugin) => plugin.onMouseMove?.(e));
    };

    // #endregion
}
